export interface TextUndoStackState {
  stack: string[];
  redoStack: string[];
  committed: string;
}

export function createTextUndoStackState(initial: string): TextUndoStackState {
  return { stack: [], redoStack: [], committed: initial };
}

export function pushTextUndoSnapshot(
  state: TextUndoStackState,
  maxStack: number,
): TextUndoStackState {
  const stack = [...state.stack, state.committed];
  return {
    committed: state.committed,
    redoStack: [],
    stack: stack.length > maxStack ? stack.slice(-maxStack) : stack,
  };
}

export function applyUserTextEdit(
  state: TextUndoStackState,
  next: string,
  maxStack: number,
): TextUndoStackState | null {
  if (next === state.committed) return null;
  const withSnapshot = pushTextUndoSnapshot(state, maxStack);
  return { stack: withSnapshot.stack, redoStack: [], committed: next };
}

export function applyExternalTextReplace(
  state: TextUndoStackState,
  next: string,
  maxStack: number,
): TextUndoStackState {
  if (next === state.committed) return state;
  const withSnapshot = pushTextUndoSnapshot(state, maxStack);
  return { stack: withSnapshot.stack, redoStack: [], committed: next };
}

export function popTextUndo(state: TextUndoStackState): { state: TextUndoStackState; restored: string } | null {
  if (state.stack.length === 0) return null;
  const stack = [...state.stack];
  const restored = stack.pop()!;
  return {
    state: {
      stack,
      redoStack: [...state.redoStack, state.committed],
      committed: restored,
    },
    restored,
  };
}

export function popTextRedo(state: TextUndoStackState): { state: TextUndoStackState; restored: string } | null {
  if (state.redoStack.length === 0) return null;
  const redoStack = [...state.redoStack];
  const restored = redoStack.pop()!;
  return {
    state: {
      stack: [...state.stack, state.committed],
      redoStack,
      committed: restored,
    },
    restored,
  };
}

export function seedTextUndoBaseline(state: TextUndoStackState, baseline: string): TextUndoStackState {
  return { stack: [...state.stack, baseline], redoStack: [], committed: state.committed };
}
